/**
 * Générateur — Cercle circonscrit à un triangle
 * Attendu ID : 671 | Niveau : 6ème | Domaine : Espace et géométrie
 *
 * Types générés (1 catégorie, 6 formats) :
 *   - centre_cercle_circonscrit_qcm         : QCM 4 choix (position du centre)
 *   - rayon_saisie                          : saisie numérique (OA = R)
 *   - etapes_construction_ordonner          : 4 étapes à ordonner (procédure)
 *   - caracterisation_cercle_vf             : V/F 3V + 3F (discrimine inscrit/circonscrit)
 *   - association_objet_definition_relier   : relier objet ↔ définition (4 paires)
 *   - construction_qcm                      : QCM 4 choix (méthode / outils)
 *
 * Spécificité : 100 % texte pur (pas de SVG). O est toujours le centre du cercle
 * circonscrit (réservation lettre). 22 lettres effectives (O exclu du pool des
 * sommets), 13 valeurs pour les longueurs (2..8 pas 0,5).
 *
 * Partition avec attendus voisins :
 *   - 670 : concours des médiatrices (propriété + preuve).
 *   - 671 : cercle circonscrit (conséquence, construction) — ce générateur.
 *   - 660/655-657 : prérequis cluster 30.
 *
 * Variantes estimées : plusieurs dizaines de milliers par format (objectif :
 * 20/20 uniques sur 20 itérations auditGen, stable sur 5 runs).
 */

import {
    randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeOrdonner,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 671;
// Pool effectif : 22 lettres. O est réservé au centre du cercle circonscrit,
// I exclu pour éviter la confusion avec le chiffre 1.
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('').filter(c => c !== 'O');

const L_CENTRE = 'Position du centre du cercle circonscrit (QCM)';
const L_RAYON  = 'Rayon du cercle circonscrit (saisie)';
const L_ETAPES = 'Étapes de construction (ordering)';
const L_VF     = 'Caractérisation du cercle circonscrit (V/F)';
const L_RELIER = 'Association objet ↔ définition (relier)';
const L_METHO  = 'Méthode de construction (QCM)';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_CENTRE,
});

const pickLetters = (n) => pickLettresSains(LETTRES, n);

// Format français pour les décimaux (virgule, supprime la décimale si entière).
const fr = (x) => {
    if (Number.isInteger(x)) return String(x);
    return String(x).replace('.', ',');
};

// ─── Format 1 — Position du centre (QCM) ─────────────────────

const genCentreCercleCirconscrit = () => {
    const [L1, L2, L3] = pickLetters(3);
    let choix, bonne, texte;

    if (Math.random() < 0.5) {
        // V1 — Où est situé le centre ?
        // « médianes » et « hauteurs » n'étant pas rencontrées en 6ème, elles
        // ne portaient aucune erreur possible : l'élève ne pouvait que tirer au
        // hasard entre deux mots inconnus. On garde « bissectrices », qui porte
        // la confusion classique sommets / côtés, et on formule les deux autres
        // avec le vocabulaire du niveau.
        bonne = 'Au point de concours des médiatrices des trois côtés.';
        choix = [
            bonne,
            'Au point de concours des bissectrices des trois angles.',
            'Au point situé à égale distance des trois côtés.',
            'Au milieu du plus grand côté du triangle.',
        ];
        texte = `<p>Dans un triangle ${L1}${L2}${L3}, où est situé le centre du cercle circonscrit ?</p>`;
    } else {
        // V2 — Comment construit-on le centre ?
        bonne = 'On trace les médiatrices de deux côtés : leur intersection est le centre.';
        choix = [
            bonne,
            'On trace les bissectrices de deux angles : leur intersection est le centre.',
            'On prend le milieu du plus grand côté.',
            // Remplace « le point d'intersection des diagonales », qui ne
            // décrivait rien : un triangle n'a pas de diagonale. Celui-ci porte
            // l'erreur de construction la plus fréquente — la perpendiculaire
            // au côté tracée par le sommet au lieu du milieu.
            'On trace les perpendiculaires aux côtés passant par les sommets.',
        ];
        texte = `<p>Comment construit-on le centre du cercle circonscrit à un triangle ${L1}${L2}${L3} ?</p>`;
    }

    const payload = makeQcm(choix, bonne, texte, opts(L_CENTRE));
    payload._meta.generatorKey = 'centre_cercle_circonscrit_qcm';
    return payload;
};

// ─── Format 2 — Rayon (saisie) ───────────────────────────────

const DISTANCES_POOL = [2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8];

const genRayonSaisie = () => {
    const [L1, L2, L3] = pickLetters(3);
    const r = randomChoice(DISTANCES_POOL);

    let texte;
    if (Math.random() < 0.5) {
        // V1 — rayon déduit de OA
        texte = `<p>Le cercle circonscrit au triangle ${L1}${L2}${L3} a pour centre O. Sachant que O${L1} = ${fr(r)} cm, quelle est la longueur du rayon R en cm ?</p>`;
    } else {
        // V2 — longueur entre centre et autre sommet
        texte = `<p>Dans un triangle ${L1}${L2}${L3}, O est le centre du cercle circonscrit. On sait que O${L2} = ${fr(r)} cm. Quelle est la longueur O${L1} en cm ?</p>`;
    }

    const payload = makeSaisie(fr(r), texte, {
        ...opts(L_RAYON),
        suffixe: ' cm',
        strict: false,
    });
    payload._meta.generatorKey = 'rayon_saisie';
    return payload;
};

// ─── Format 3 — Étapes de construction (ordering) ────────────

// Les 3 arrangements possibles de 2 côtés cités dans la construction.
// Chaque entrée : { cites: [[X1,X2], [Y1,Y2]], commun: S }
// où S est le sommet commun aux deux côtés (partagé entre les 2 médiatrices tracées).
const _arrangementsCotesConstruction = (L1, L2, L3) => [
    { cites: [[L1, L2], [L2, L3]], commun: L2 },
    { cites: [[L1, L2], [L1, L3]], commun: L1 },
    { cites: [[L2, L3], [L1, L3]], commun: L3 },
];

const genEtapesConstruction = () => {
    const [L1, L2, L3] = pickLetters(3);
    const arrangements = _arrangementsCotesConstruction(L1, L2, L3);
    const { cites, commun } = randomChoice(arrangements);
    const [X1, X2] = cites[0];
    const [Y1, Y2] = cites[1];
    const S = commun;

    const items = [
        `Tracer les médiatrices des côtés [${X1}${X2}] et [${Y1}${Y2}].`,
        'Noter O le point d\'intersection des deux médiatrices.',
        `Régler l'écartement du compas à la longueur O${S}.`,
        `Tracer le cercle de centre O et de rayon O${S}.`,
    ];

    const texte = `<p>Voici les étapes de la construction du cercle circonscrit au triangle ${L1}${L2}${L3}. Remets-les dans le bon ordre.</p>`;

    const payload = makeOrdonner(items, texte, opts(L_ETAPES));
    payload._meta.generatorKey = 'etapes_construction_ordonner';
    return payload;
};

// ─── Format 4 — Caractérisation (V/F) ────────────────────────

const buildAffirmationsVF = () => {
    const [L1, L2, L3] = pickLetters(3);
    return {
        VRAIS: {
            V1: () => ({
                id: 'V1', verite: 0,
                texte: 'Le cercle circonscrit à un triangle passe par ses trois sommets.',
            }),
            V2: () => ({
                id: 'V2', verite: 0,
                texte: `Le centre du cercle circonscrit au triangle ${L1}${L2}${L3} est le point équidistant de ${L1}, ${L2} et ${L3}.`,
            }),
            V3: () => ({
                id: 'V3', verite: 0,
                texte: 'Tout triangle admet un unique cercle circonscrit.',
            }),
            V4: () => ({
                id: 'V4', verite: 0,
                texte: 'Le centre du cercle circonscrit à un triangle se trouve à l\'intersection des médiatrices de ses côtés.',
            }),
            V5: () => ({
                id: 'V5', verite: 0,
                texte: `Si le cercle circonscrit au triangle ${L1}${L2}${L3} a pour centre O et pour rayon R, alors O${L1} = O${L2} = O${L3} = R.`,
            }),
        },
        FAUX: {
            F1: () => ({
                id: 'F1', verite: 1,
                texte: 'Le cercle circonscrit à un triangle passe par les milieux des trois côtés.',
            }),
            F2: () => ({
                id: 'F2', verite: 1,
                texte: 'Le centre du cercle circonscrit est le point d\'intersection des bissectrices.',
            }),
            F3: () => ({
                id: 'F3', verite: 1,
                texte: 'Le rayon du cercle circonscrit est égal à la moitié du plus grand côté du triangle.',
            }),
            F4: () => ({
                id: 'F4', verite: 1,
                texte: `Dans le triangle ${L1}${L2}${L3}, le centre du cercle circonscrit est toujours situé à l'intérieur du triangle.`,
            }),
            F5: () => ({
                id: 'F5', verite: 1,
                texte: 'Un triangle peut avoir deux cercles circonscrits distincts.',
            }),
        },
    };
};

const genCaracterisationVF = () => {
    let tries = 0;
    while (tries < 5) {
        const builders = buildAffirmationsVF();
        const idsV = randomChoiceMultiple(Object.keys(builders.VRAIS), 3);
        const idsF = randomChoiceMultiple(Object.keys(builders.FAUX), 3);
        const vrais = idsV.map(id => builders.VRAIS[id]());
        const faux  = idsF.map(id => builders.FAUX[id]());
        const mixed = shuffle([...vrais, ...faux]);

        const lignes = mixed.map(a => a.texte);
        const bonnesReponses = mixed.map(a => a.verite);

        if (new Set(lignes).size === lignes.length) {
            const texte = `<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`;
            const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
            payload._meta.generatorKey = 'caracterisation_cercle_vf';
            return payload;
        }
        tries++;
    }

    // Fallback déterministe
    const builders = buildAffirmationsVF();
    const vrais = ['V1', 'V2', 'V3'].map(id => builders.VRAIS[id]());
    const faux  = ['F1', 'F3', 'F5'].map(id => builders.FAUX[id]());
    const mixed = shuffle([...vrais, ...faux]);
    const lignes = mixed.map(a => a.texte);
    const bonnesReponses = mixed.map(a => a.verite);
    const texte = `<p>Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
    payload._meta.generatorKey = 'caracterisation_cercle_vf';
    return payload;
};

// ─── Format 5 — Association objet ↔ définition ────────────────

const genAssociationRelier = () => {
    const [L1, L2, L3] = pickLetters(3);

    const pairs = [
        {
            source: `Cercle circonscrit au triangle ${L1}${L2}${L3}`,
            target: `Cercle qui passe par ${L1}, ${L2} et ${L3}`,
        },
        {
            source: 'Centre du cercle circonscrit',
            target: 'Point de concours des médiatrices des côtés',
        },
        {
            source: 'Rayon du cercle circonscrit',
            target: 'Distance du centre O à un sommet du triangle',
        },
        {
            source: 'Médiatrice d\'un côté du triangle',
            target: 'Droite perpendiculaire à ce côté passant par son milieu',
        },
    ];

    const texte = `<p>Dans le triangle ${L1}${L2}${L3}, associe chaque objet à sa définition.</p>`;

    const payload = makeRelier(pairs, texte, opts(L_RELIER));
    payload._meta.generatorKey = 'association_objet_definition_relier';
    return payload;
};

// ─── Format 6 — Méthode de construction (QCM) ────────────────

const genConstructionQcm = () => {
    const [L1, L2, L3] = pickLetters(3);
    const r = Math.random();
    let choix, bonne, texte;

    if (r < 1 / 3) {
        // V1 — Nombre de médiatrices nécessaires
        bonne = '2';
        choix = ['2', '1', '3', '0'];
        texte = `<p>Pour construire le centre du cercle circonscrit à un triangle ${L1}${L2}${L3}, combien de médiatrices suffit-il de tracer ?</p>`;
    } else if (r < 2 / 3) {
        // V2 — Réglage du compas (cite un sommet au hasard)
        const sommet = randomChoice([L1, L2, L3]);
        const autre = randomChoice([L1, L2, L3].filter(x => x !== sommet));
        bonne = `À la longueur O${sommet}.`;
        choix = [
            bonne,
            `À la longueur ${sommet}${autre}.`,
            `À la longueur ${sommet}${autre} divisée par 2.`,
            'Au hasard.',
        ];
        texte = `<p>Après avoir tracé le centre O du cercle circonscrit d'un triangle ${L1}${L2}${L3}, comment règle-t-on l'écartement du compas pour tracer le cercle ?</p>`;
    } else {
        // V3 — Pourquoi le cercle passe par les 3 sommets
        bonne = `Parce que O${L1} = O${L2} = O${L3}.`;
        choix = [
            bonne,
            `Parce que le triangle ${L1}${L2}${L3} est équilatéral.`,
            'Parce qu\'il y a trois médiatrices dans un triangle.',
            // Remplace « C'est une coïncidence géométrique. », que personne ne
            // cochait : celui-ci porte une confusion réelle (croire le centre
            // plus proche de deux sommets que du troisième).
            `Parce que O${L2} et O${L3} sont plus courtes que O${L1}.`,
        ];
        texte = `<p>Pourquoi le cercle de centre O (point de concours des médiatrices du triangle ${L1}${L2}${L3}) et de rayon O${L1} passe-t-il aussi par ${L2} et ${L3} ?</p>`;
    }

    const payload = makeQcm(choix, bonne, texte, opts(L_METHO));
    payload._meta.generatorKey = 'construction_qcm';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    centre_cercle_circonscrit_qcm:           genCentreCercleCirconscrit,
    rayon_saisie:                            genRayonSaisie,
    etapes_construction_ordonner:            genEtapesConstruction,
    caracterisation_cercle_vf:               genCaracterisationVF,
    association_objet_definition_relier:     genAssociationRelier,
    construction_qcm:                        genConstructionQcm,
};

export const CATEGORIES = [
    {
        id: 'connaissance_cercle_circonscrit',
        label: 'Connaître le cercle circonscrit',
        formatsList: [
            { key: 'centre_cercle_circonscrit_qcm',       label: 'QCM' },
            { key: 'caracterisation_cercle_vf',           label: 'V/F' },
            { key: 'association_objet_definition_relier', label: 'Relier' },
        ],
    },
    {
        id: 'construction_cercle_circonscrit',
        label: 'Construire le cercle circonscrit',
        formatsList: [
            { key: 'etapes_construction_ordonner', label: 'Ordonner' },
            { key: 'construction_qcm',             label: 'QCM' },
        ],
    },
    {
        id: 'application_cercle_circonscrit',
        label: 'Appliquer (OA = R)',
        formatsList: [
            { key: 'rayon_saisie', label: 'Saisie' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

// Exposé pour tests unitaires.
export const _internals = {
    LETTRES,
    DISTANCES_POOL,
    pickLetters,
    _arrangementsCotesConstruction,
    buildAffirmationsVF,
    genCentreCercleCirconscrit,
    genRayonSaisie,
    genEtapesConstruction,
    genCaracterisationVF,
    genAssociationRelier,
    genConstructionQcm,
};
